import React, {createContext, useContext} from "react";
import {Box, Stack, TableCell, Tooltip, Typography} from "@mui/material";
import {Desk} from "../../api/Model";
import {VirtualTable} from "../../components/data-table/VirtualTable";
import {TableColumnData} from "../../components/data-table/TableColumnData";
import EmployeeAvatar from "../../components/employee-avatar/EmployeeAvatar";
import EmployeeLink from "../../components/employee-link/EmployeeLink";
import NeighbourhoodColorIcon from "../../components/neighbourhood-icon/NeighbourhoodColorIcon";
import {keyFn} from "../../util/FunctionUtil";
import {PRINT_TARGET} from "../../util/PrintUtil";
import {SiteChip} from "../../components/site-chip/SiteChip";
import {DeskModel, DeskModelImpl, DeskTableRow} from "./DeskModel";
import WarningIcon from "@mui/icons-material/Error";


function EmployeeCell({desk}: {
    desk: Desk,
}) {
    const model = useContext(DeskTableContext);
    const employee = model.employee(desk);
    const neighbourhoodMismatch = model.neighbourhoodMismatch(desk);

    return (
        <TableCell>
            {employee ? (
                <Stack direction="row" alignItems="center" gap={1} justifyContent="space-between">
                    <Stack direction="row" alignItems="center" gap={1}>
                        <EmployeeAvatar employee={employee} sx={{width: 30, height: 30}}/>
                        <EmployeeLink employee={employee}/>
                        <SiteChip employee={employee}/>
                    </Stack>
                    {neighbourhoodMismatch && (
                        <Tooltip title={
                            <>
                                <Typography>{neighbourhoodMismatch.deskNeighbourhood ? (
                                    `The desk is assigned to neighbourhood "${neighbourhoodMismatch.deskNeighbourhood.name}".`
                                ) : (
                                    `The desk is not assigned to any neighbourhood.`
                                )}</Typography>
                                <Typography>{neighbourhoodMismatch.employeeNeighbourhoods.length > 0 ? (
                                    `The employee is a member of neighbourhood ${neighbourhoodMismatch.employeeNeighbourhoods.map(it => `"${it.name}"`).join(', ')}.`
                                ) : (
                                    'The employee is not a member of any neighbourhood.'
                                )}</Typography>
                            </>
                        }>
                            <Stack direction="row" alignItems="center" aria-label="Neighbourhood mismatch">
                                {neighbourhoodMismatch.employeeNeighbourhoods.map((hood, index) => (
                                    <NeighbourhoodColorIcon key={hood.key} neighbourhood={hood} sx={{ml: index > 0 ? -1 : 0}}/>
                                ))}
                                <WarningIcon sx={{fontSize: '16px', ml: '-12px', alignSelf: 'end', border: '1px solid white', backgroundColor: 'white', borderRadius: '50%'}} color="warning"/>
                            </Stack>
                        </Tooltip>
                    )}
                </Stack>
            ) : (
                <Typography variant="body2" color="text.secondary">Shared</Typography>
            )}
        </TableCell>
    );
}

function NeighbourhoodCell({desk}: {
    desk: Desk,
}) {
    const model = useContext(DeskTableContext);
    const neighbourhood = model.neighbourhood(desk);

    return (
        <TableCell>
            {neighbourhood ? (
                <Stack direction="row" alignItems="center" gap={1}>
                    <NeighbourhoodColorIcon neighbourhood={neighbourhood} size={16}/>
                    {neighbourhood.name}
                </Stack>
            ) : undefined}
        </TableCell>
    );
}

function VisitsCell({desk}: { desk: Desk }) {
    const model = useContext(DeskTableContext);
    const stats = model.employeeVisitStats(desk);

    return (
        <TableCell>
            {
                stats ? `${stats.stats.workDayVisits}${!Number.isNaN(stats.stats.percent) ? ` (${stats.stats.percent}%)` : ''}`
                    : model.employee(desk) ? '—'
                        : undefined
            }
        </TableCell>
    );
}

function ActionCell({desk}: { desk: Desk }) {
    const model = useContext(DeskTableContext);

    return (
        <TableCell align="right">
            {model.actionButton(desk)}
        </TableCell>
    );
}


const columns: TableColumnData<DeskTableRow>[] = [
    {
        key: 'name',
        header: 'Desk',
        width: '16em',
        cell: row => (
            row.type === 'floor' ? (
                <TableCell colSpan={5} sx={{borderBottom: 'none'}}>
                    <Typography variant="h5">{row.name}</Typography>
                </TableCell>
            ) : row.type === 'room' ? (
                <TableCell colSpan={5}>
                    <Typography variant="h6">{row.name}</Typography>
                </TableCell>
            ) : (
                <TableCell>
                    {row.desk.name}
                </TableCell>
            )
        ),
    },
    {
        key: 'employee',
        header: 'Assigned to',
        width: '28em',
        cell: row => row.type === 'desk' ? (
            <EmployeeCell desk={row.desk}/>
        ) : undefined,
    },
    {
        key: 'visits',
        header: 'Visits',
        width: '8em',
        cell: row => row.type === 'desk' ? (
            <VisitsCell desk={row.desk}/>
        ) : undefined,
    },
    {
        key: 'neighbourhood',
        header: 'Neighbourhood',
        cell: row => row.type === 'desk' ? (
            <NeighbourhoodCell desk={row.desk}/>
        ) : undefined,
    },
    {
        key: 'actions',
        header: '',
        width: '64px',
        align: 'right',
        cell: row => row.type === 'desk' ? (
            <ActionCell desk={row.desk}/>
        ) : undefined,
    },
];

const DeskTableContext = createContext<DeskModel>(DeskModelImpl.EMPTY);

export function DeskTable({model, rows}: {
    model: DeskModel,
    rows: DeskTableRow[],
}) {
    return (
        <Box className={PRINT_TARGET}>
            <DeskTableContext value={model}>
                <VirtualTable rows={rows} columns={columns} id={keyFn}/>
            </DeskTableContext>
        </Box>
    );
}
